import React, { useCallback, useState } from 'react';

import api, {
    type AddPassengerToTravelFile,
    type BodyEditPassengerTravelFile,
    type RemovePassengerFromTravelFile
} from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Passenger } from '../types';
import { PassengerQueryType } from '../helpers';
import { ModelOptions } from '../../../types';

import Context from './context';

interface Props {
    children: React.ReactNode;
}

const PassengersProvider: React.FC<Props> = ({ children }: Props) => {
    const [data, setData] = useState<Passenger[]>([]);
    const [selected, setSelected] = useState<Passenger | null>(null);
    const [error, setError] = useState<unknown>({});

    const handleGetData = useCallback((res: Passenger[]) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    const getOne = useGetOne(api, setSelected, setError);

    const handleSetData = useCallback((_item: Passenger, newData: Passenger[]) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id: string | number, newData: Passenger[]) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);

    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const addPassengerToTravelFile = useCallback(async (body: AddPassengerToTravelFile) => {
        try {
            return await api.addPassengerToTravelFile(body);
        } catch (err) {
            setError(err);
            throw err;
        }
    }, []);

    // Returns the updated travel file (same shape as addPassengerToTravelFile) so
    // callers can push it straight into travelFilesContext.setSelected. It used to
    // discard the response and `return get()` instead, refetching the whole
    // passengers table — 141k rows — on every single passenger edit, which left the
    // form spinning and still gave the caller the wrong shape.
    const editPassengerTravelFile = useCallback(async (body: BodyEditPassengerTravelFile) => {
        try {
            return await api.editPassengerTravelFile(body);
        } catch (err) {
            setError(err);
            throw err;
        }
    }, []);

    const removePassengerFromTravelFile = useCallback(async (body: RemovePassengerFromTravelFile) => {
        try {
            return await api.removePassengerFromTravelFile(body);
        } catch (err) {
            setError(err);
            throw err;
        }
    }, []);

    const reinstatePassengerToTravelFile = useCallback(async (body: RemovePassengerFromTravelFile) => {
        try {
            return await api.reinstatePassengerToTravelFile(body);
        } catch (err) {
            setError(err);
            throw err;
        }
    }, []);

    const query = useCallback(async (query: PassengerQueryType, options?: ModelOptions) => {
        try {
            return await api.query(query, options);
        } catch (err) {
            setError(err);
            throw err;
        }
    }, []);

    return (
        <Context.Provider
            value={{
                get,
                getOne,
                post,
                put,
                data,
                setData,
                selected,
                setSelected,
                remove,
                error,
                softDelete,
                addPassengerToTravelFile,
                editPassengerTravelFile,
                removePassengerFromTravelFile,
                reinstatePassengerToTravelFile,
                query
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default PassengersProvider;
